import { useState } from 'react'
import { capitalize } from 'lodash'
import { Box, Button, Chip, Skeleton, Stack, Typography } from '@mui/material'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import Confirm from 'components/Dialog'
import { HomeIcon } from 'components/Icons'
import formatAddress from 'utils/formatAddress'
import { needsMoreProof } from 'utils/proofOfAddress'
import { ADDRESS_CHANGE_STATUS_COLORS } from 'constants'

const names = people => (people ?? []).map(person => `${person.firstName} ${person.lastName}`).join(', ')

export default function AddressChangeRequestCard({ loading, request, canRequest, onRequest, onEdit, onCancel }) {
  const { t } = useTranslation()
  const [confirm, setConfirm] = useState(false)
  const [cancelling, setCancelling] = useState(false)

  if (loading) return <Skeleton variant="rectangular" height={72} />
  if (!request && !canRequest) return null

  const isPending = request?.status === 'pending'
  const actionNeeded = isPending && needsMoreProof(request.proofOfAddress)

  const onConfirm = async () => {
    if (cancelling) return
    setCancelling(true)
    await onCancel(request)
    setCancelling(false)
    setConfirm(false)
  }

  const details = request && [
    `${t('Submitted')} ${dayjs(request.createdAt).format('MM/DD/YYYY')}`,
    `${t('Students')}: ${names(request.students)}`,
    request.secondaryParents?.length > 0 && `${t('Other Parents')}: ${names(request.secondaryParents)}`,
  ]

  return (
    <Box
      data-component="AddressChangeRequestCard"
      sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2, px: 2.5, py: 1.5, bgcolor: 'action.hover' }}
    >
      <HomeIcon color="action" />

      <Box sx={{ flex: 1, minWidth: 240 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="subtitle2">{t('Address Change Request')}</Typography>
          {request && <Chip label={t(capitalize(request.status))} color={ADDRESS_CHANGE_STATUS_COLORS[request.status]} size="small" />}
        </Stack>
        {request ? (
          <>
            <Typography variant="body2">{formatAddress(request.address)}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {details.filter(Boolean).join(' · ')}
            </Typography>
            {actionNeeded ? (
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                <Chip size="small" color="error" label={t('Action needed')} />
                <Typography variant="caption" color="error.main">
                  {t('The school rejected one or more proof of address files. Update the files to continue.')}
                </Typography>
              </Stack>
            ) : (
              isPending && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {t('Your request will be reviewed by the school before the address is updated.')}
                </Typography>
              )
            )}
          </>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {t('Moved? Request an address change for your family.')}
          </Typography>
        )}
      </Box>

      <Stack direction="row" spacing={1}>
        {isPending && onEdit && (
          <Button size="small" variant="outlined" onClick={() => onEdit(request)}>
            {t('Update Files')}
          </Button>
        )}
        {isPending && (
          <Button size="small" variant="outlined" color="error" onClick={() => setConfirm(true)}>
            {t('Cancel Request')}
          </Button>
        )}
        {canRequest && (
          <Button size="small" variant="contained" onClick={onRequest}>
            {t('Request Address Change')}
          </Button>
        )}
      </Stack>

      <Confirm
        open={confirm}
        onClose={() => !cancelling && setConfirm(false)}
        onConfirm={onConfirm}
        dialogTitle={t('Cancel Request')}
        message={t('Are you sure you want to cancel this address change request?')}
      />
    </Box>
  )
}
